import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { ProposalsService } from "src/app/pages/proposals/services/proposals.service";
import Swal from "sweetalert2";
import { EventService } from "src/app/core/services/event.service";
import { PropertyDealsModel, ProposerModel } from "./models/proposer.model";
import { Subscription } from "rxjs";
import { auditTime } from "rxjs/operators";
import { Deals } from "src/app/pages/deals/models/deals.model";
import { EntityService } from "src/app/pages/entity/services/entity.service";
import { PropertiesService } from "src/app/pages/properties/services/properties.services";
import { ActivatedRoute } from "@angular/router";
import { AuthService } from "src/app/core/services/auth.service";

@Component({
  selector: "app-proposal-modal",
  templateUrl: "./proposal.component.html",
  styleUrls: ["./proposal.component.scss"],
})
export class ProposalComponent implements OnInit, OnDestroy {
  @Input() deal!: Deals;
  @Input() leadId: any;
  @Input() proposal: any;
  @Input() isEdit: boolean = false;
  proposalForm!: FormGroup;
  fullProposalForm!: FormGroup;
  proposerForm!: FormGroup;
  @ViewChild("content", { static: true }) content!: ElementRef;
  submited: boolean = false;
  proposalType!: "full" | "resume";
  // Chaves de tradução: o template passa-as pelo `translate`.
  steps = [
    "t-proposal-form-step-proposers",
    "t-proposal-form-step-proposal",
    "t-proposal-form-step-mediation",
  ];

  /** Rótulo curto do tipo de proponente, no cartão. */
  proposerTypeKey(type: string | null | undefined): string {
    if (type === "INDIVIDUAL") return "t-proposal-form-proposer-individual-short";
    if (type === "LEGAL") return "t-proposal-form-proposer-legal-short";
    if (type === "EFFECTIVE_BENEFICIARY") {
      return "t-proposal-form-proposer-beneficiary-short";
    }
    return "t-proposal-form-proposer-guarantor";
  }
  currentStep = 0;
  proposers: ProposerModel[] = [];
  showError: { [key: string]: boolean } = {};
  proposalData: any;
  showProposal: boolean = false;
  subscriptions: Subscription = new Subscription();
  proposalFormChangesSub?: Subscription;
  fullProposalFormChangesSub?: Subscription;
  entityData: any;
  spinnerLoading: boolean = false;
  pendingProposalsLimit: number | null = null;

  // Internal share: partner store (the sender's store) chosen when the sender
  // belongs to more than one store; otherwise the backend uses the first one.
  internalSharePartnerStoreId: number | null = null;

  get internalShareStoreOptions(): { id: number; name: string }[] {
    const user = this.authService.currentUserValue;
    return (user?.stores ?? [])
      .filter((store: any) => typeof store === "object" && store?.id)
      .map((store: any) => ({
        id: Number(store.id),
        name: store.name ?? `${store.id}`,
      }));
  }

  // Internal share: the proposal targets a property from another store of the
  // same entity — the property's store will have to validate it.
  get isInternalShareProposal(): boolean {
    const user = this.authService.currentUserValue;
    const property: any = this.deal?.property ?? this.proposal?.property;
    const propertyStoreId = property?.store?.id ?? property?.storeId;
    if (!user || user?.role?.name === "MASTER" || !propertyStoreId) {
      return false;
    }
    const storeIds = (user.stores ?? []).map((store: any) =>
      typeof store === "object" ? store?.id ?? store?.storeId : store,
    );
    return (
      storeIds.length > 0 &&
      !storeIds.some((id: any) => Number(id) === Number(propertyStoreId))
    );
  }

  get isSaleBusinessType(): boolean {
    return this.resolveProposalBusinessType() === "SALE";
  }

  get isRentBusinessType(): boolean {
    return this.resolveProposalBusinessType() === "RENT";
  }

  constructor(
    private formBuilder: FormBuilder,
    private modalService: NgbModal,
    private proposalsService: ProposalsService,
    private entityService: EntityService,
    private propertiesService: PropertiesService,
    private eventService: EventService,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    public authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.entityService.getEntityForProposal().subscribe((data) => {
      this.entityData = data;
      this.pendingProposalsLimit = data?.pendingProposalsLimit ?? null;

      if (!this.isEdit) {
        this.validateLimitAndOpenNewProposal(data);
      } else {
        if (this.proposal) {
          if (
            this.proposal?.proposal?.proposalProposers &&
            this.proposal?.proposal?.proposalProposers?.length > 0
          ) {
            this.proposalsService
              .getProposalById(this.proposal.proposal?.id)
              .subscribe((data) => {
                if (data) {
                  this.proposers = data?.proposalProposers;
                  this.initFullProposal(data);
                  this.proposalType = "full";
                }
              });
          } else {
            this.proposalForm = this.formBuilder.group({
              propertyId: [this.proposal.property?.id],
              lead: [this.proposal?.proposal?.leadId],
              contact: [this.proposal?.proposal?.contactId],
              agencyProposal: [this.proposal?.proposal?.agencyProposal],
              partnerName: [this.proposal?.proposal?.partnerName],
              licenseImo: [this.proposal?.proposal?.licenseImo],
              saleDivision: [this.proposal?.proposal?.saleDivision ?? 50],
              clientName: [this.proposal?.proposal?.clientName],
              proposalDate: [
                this.proposal?.proposal?.proposalDate ?? new Date(),
              ],
              value: [
                this.proposal?.proposal?.proposalsHistory?.[0]?.value ??
                  this.deal.property?.price,
                [Validators.required, Validators.min(1)],
              ],
              obs: [this.proposal?.proposal?.firstProposal?.obs],
              file: [null],
            });
            this.proposalType = "resume";
            this.bindProposalDraftSync();
            this.openModal();
          }
        }
      }
    });

    const sub = this.eventService.subscribe("proposal-pdf-generated", () => {
      this.spinnerLoading = false;
      this.showProposal = false;
      this.modalService.dismissAll();
    });
    this.subscriptions.add(sub);
  }

  private validateLimitAndOpenNewProposal(entity?: any) {
    if (entity) {
      this.entityData = entity;
      this.pendingProposalsLimit = entity?.pendingProposalsLimit ?? null;
    }

    if (!this.deal?.property?.id) {
      this.newProposal();
      return;
    }
    const propertyId = this.deal.property.id;

    const limit = Number(this.pendingProposalsLimit ?? 0);
    if (!limit || limit <= 0) {
      this.newProposal();
      return;
    }

    this.propertiesService.findOne(propertyId).subscribe({
      next: (propertyData) => {
        const proposals = propertyData?.proposals ?? [];
        const activeProposals = this.getActiveProposalsCount(proposals);
        if (activeProposals >= limit) {
          this.proposalLimitMsg(this.getLimitReachedMessage());
          this.eventService.broadcast("new-proposal-closed");
          return;
        }

        this.newProposal();
      },
      error: () => {
        this.errorMsg(this.translateService.instant("t-error-creating"));
        this.eventService.broadcast("new-proposal-closed");
      },
    });
  }

  private getActiveProposalsCount(proposals: any[]) {
    return (proposals ?? []).filter(
      (proposal) => !this.isCancelledProposal(proposal),
    ).length;
  }

  private isCancelledProposal(proposal: any) {
    const status = this.getCurrentProposalStatus(proposal);
    return ["declined", "canceled", "cancelled", "recusad", "cancelad"].some(
      (value) => status.includes(value),
    );
  }

  private getCurrentProposalStatus(proposal: any) {
    return String(
      proposal?.lastAction?.originalStatus ??
        proposal?.history?.[0]?.originalStatus ??
        proposal?.lastAction?.proposalStatus ??
        proposal?.history?.[0]?.proposalStatus ??
        "",
    ).toLowerCase();
  }

  private getLimitReachedMessage() {
    const translateKey = "t-pending-proposals-limit-reached";
    const message = this.translateService.instant(translateKey);
    return message !== translateKey
      ? message
      : this.translateService.instant("t-pending-proposals-limit-reached-fallback");
  }

  private isPendingProposalLimitError(error: any): boolean {
    const messages = this.extractErrorMessages(error);
    return messages.some((message) => {
      const normalized = String(message ?? "").trim();
      return (
        normalized === "Property does not accept more proposals" ||
        normalized.includes("Property does not accept more proposals")
      );
    });
  }

  private extractErrorMessages(error: any): string[] {
    if (error === null || error === undefined) return [];

    if (typeof error === "string") {
      return [error];
    }

    if (Array.isArray(error)) {
      return error.flatMap((item) => this.extractErrorMessages(item));
    }

    if (typeof error === "object") {
      if ("message" in error) {
        return this.extractErrorMessages((error as any).message);
      }
      if ("error" in error) {
        return this.extractErrorMessages((error as any).error);
      }
    }

    return [String(error)];
  }

  private broadcastProposalDraft() {
    const proposalDraft =
      this.proposalType === "full"
        ? (this.fullProposalForm?.getRawValue?.() ?? null)
        : (this.proposalForm?.getRawValue?.() ?? null);

    this.eventService.broadcast("proposal-form-updated", proposalDraft);
  }

  private bindProposalDraftSync() {
    this.proposalFormChangesSub?.unsubscribe();
    this.fullProposalFormChangesSub?.unsubscribe();

    if (this.proposalForm) {
      this.proposalFormChangesSub = this.proposalForm.valueChanges
        .pipe(auditTime(150))
        .subscribe(() => this.broadcastProposalDraft());
      this.subscriptions.add(this.proposalFormChangesSub);
    }

    if (this.fullProposalForm) {
      this.fullProposalFormChangesSub = this.fullProposalForm.valueChanges
        .pipe(auditTime(150))
        .subscribe(() => this.broadcastProposalDraft());
      this.subscriptions.add(this.fullProposalFormChangesSub);
    }

    this.broadcastProposalDraft();
  }

  newProposal() {
    this.proposalForm = this.formBuilder.group({
      propertyId: [this.deal.property?.id, [Validators.required]],
      lead: [null],
      contact: [null],
      agencyProposal: [false],
      partnerName: [null],
      licenseImo: [null],
      saleDivision: [50],
      clientName: [this.getDefaultContactName()],
      proposalDate: [new Date()],
      value: [
        this.deal.property?.price,
        [Validators.required, Validators.min(1)],
      ],
      obs: [null],
      file: [null],
    });

    this.fullProposalForm = this.formBuilder.group({
      proposers: [null, [Validators.required]],
      proposalValue: [this.deal.property?.price, Validators.required],
      bank: [null],
      financing: [0],
      sourceFunds: [null],
      promiseContractTime: [null],
      signalBooster: [0],
      signalBooster2: [0],
      signalBooster3: [0],
      signalBooster4: [0],
      signalBoosterPercent: [false],
      signalBooster2Percent: [false],
      signalBooster3Percent: [false],
      signalBooster4Percent: [false],
      purpose: ["PERMANENT", Validators.required],
      reserveValue: [0],
      paymentMethod: ["CASH"],
      deedTime: [0],
      startDate: [null],
      securityDeposit: [0],
      contractDuration: [null],
      renewalDuration: [null],
      mediationStore: [this.resolveMediationStoreName(this.deal.property?.store)],
      responsible: [this.deal.agent?.name],
      obs: [null],
    });

    this.proposerForm = this.formBuilder.group({
      type: ["INDIVIDUAL"],
      name: [
        this.deal?.contact?.name
          ? this.deal?.contact?.name
          : this.deal?.contact?.firstName && this.deal?.contact?.lastName
            ? this.deal?.contact?.firstName + " " + this.deal?.contact?.lastName
            : this.deal?.contact?.firstName
              ? this.deal?.contact?.firstName
              : null,
        Validators.required,
      ],
      fiscalIdentification: [null, Validators.required],
      docType: [null, Validators.required],
      docNumber: [null, Validators.required],
      docExpiration: [null, Validators.required],
      maritalStatus: [null],
      propertyRegime: [null],
      naturalness: [null],
      nationality: [null],
      email: [this.deal?.contact?.email, Validators.email],
      phone: [null],
      address: [null],
      city: [null],
      postalCode: [null],
      country: [null],
      fiscalAddress: [null],
      fiscalCity: [null],
      fiscalPostalCode: [null],
      fiscalCountry: [null],
      occupation: [null],
      employer: [null],
      actionType: ["SELF"],
      actionTypeOther: [null],
      politicalPositions: [false],
      politicalDesc: [null],
      company: [null],
      companyFiscalIdentification: [null],
      permanentRegistry: [null],
    });
    this.bindProposalDraftSync();
    this.openModal();
  }

  initFullProposal(data: any) {
    this.fullProposalForm = this.formBuilder.group({
      proposers: [this.proposers, [Validators.required]],
      proposalValue: [
        data?.proposalsHistory?.[0]?.value ?? this.deal.property?.price,
        Validators.required,
      ],
      bank: [data?.bank],
      financing: [data?.financing ?? 0],
      sourceFunds: [data?.sourceFunds],
      promiseContractTime: [data?.promiseContractTime],
      signalBooster: [data?.signalBooster ?? 0],
      signalBooster2: [data?.signalBooster2 ?? 0],
      signalBooster3: [data?.signalBooster3 ?? 0],
      signalBooster4: [data?.signalBooster4 ?? 0],
      signalBoosterPercent: [data?.signalBoosterPercent ?? false],
      signalBooster2Percent: [data?.signalBooster2Percent ?? false],
      signalBooster3Percent: [data?.signalBooster3Percent ?? false],
      signalBooster4Percent: [data?.signalBooster4Percent ?? false],
      purpose: [data?.purpose ?? "PERMANENT", Validators.required],
      reserveValue: [data?.reserveValue ?? 0],
      paymentMethod: [data?.paymentMethod ?? "CASH"],
      deedTime: [data?.deedTime ?? 0],
      startDate: [data?.startDate],
      securityDeposit: [data?.securityDeposit ?? 0],
      contractDuration: [data?.contractDuration],
      renewalDuration: [data?.renewalDuration],
      mediationStore: [this.resolveMediationStoreName(data?.mediationStore ?? this.deal.property?.store)],
      responsible: [data?.responsible ?? this.deal.agent?.name],
      obs: [data?.proposalsHistory?.[0]?.obs ?? data?.firstProposal?.obs ?? null],
    });
    this.proposerForm = this.formBuilder.group({
      type: ["INDIVIDUAL"],
      name: [
        this.deal?.contact?.name
          ? this.deal?.contact?.name
          : this.deal?.contact?.firstName && this.deal?.contact?.lastName
            ? this.deal?.contact?.firstName + " " + this.deal?.contact?.lastName
            : this.deal?.contact?.firstName
              ? this.deal?.contact?.firstName
              : null,
        Validators.required,
      ],
      fiscalIdentification: [null, Validators.required],
      docType: [null, Validators.required],
      docNumber: [null, Validators.required],
      docExpiration: [null, Validators.required],
      maritalStatus: [null],
      propertyRegime: [null],
      naturalness: [null],
      nationality: [null],
      email: [this.deal?.contact?.email, Validators.email],
      phone: [null],
      address: [null],
      city: [null],
      postalCode: [null],
      country: [null],
      fiscalAddress: [null],
      fiscalCity: [null],
      fiscalPostalCode: [null],
      fiscalCountry: [null],
      occupation: [null],
      employer: [null],
      actionType: ["SELF"],
      actionTypeOther: [null],
      politicalPositions: [false],
      politicalDesc: [null],
      company: [null],
      companyFiscalIdentification: [null],
      permanentRegistry: [null],
    });
    this.bindProposalDraftSync();
    this.openModal("lg");
  }

  openModal(size: "md" | "lg" = "md") {
    let modal = this.modalService.open(this.content, {
      size: size,
      centered: true,
      backdropClass: "modal-z",
      modalDialogClass: "modal-custom-size",
      windowClass: "modal-z",
    });
    modal.closed.subscribe(() =>
      this.eventService.broadcast("new-proposal-closed"),
    );
    modal.dismissed.subscribe(() =>
      this.eventService.broadcast("new-proposal-closed"),
    );
  }

  changeProposalType() {
    if (this.proposalType === "full") {
      document.querySelector(".modal-custom-size")?.classList.add("modal-lg");
    }
    this.broadcastProposalDraft();
  }

  get form() {
    return this.proposalForm.controls;
  }

  changeType(ev: boolean) {
    if (ev) {
      this.proposalForm.controls["partnerName"].setValidators(
        Validators.required,
      );
      this.proposalForm.controls["licenseImo"].setValidators(
        Validators.required,
      );
      this.proposalForm.controls["saleDivision"].setValidators(
        Validators.required,
      );
      this.proposalForm.updateValueAndValidity();
    } else {
      const c1 = this.proposalForm.get("partnerName");
      const c2 = this.proposalForm.get("licenseImo");
      const c3 = this.proposalForm.get("saleDivision");
      c1?.clearValidators();
      c1?.updateValueAndValidity();
      c2?.clearValidators();
      c2?.updateValueAndValidity();
      c3?.clearValidators();
      c3?.updateValueAndValidity();
    }
  }

  addFileCostumer() {
    const inputFile = document.getElementById("input-filec");
    inputFile?.click();
  }

  onFileChange(event: any) {
    if (event.target.files && event.target.files.length) {
      const [file] = event.target.files;
      this.proposalForm.patchValue({
        file: file,
      });
    }
    event.target.value = "";
  }

  formatSizeUnits(bytes: any) {
    if (bytes >= 1073741824) {
      bytes = (bytes / 1073741824).toFixed(2) + " GB";
    } else if (bytes >= 1048576) {
      bytes = (bytes / 1048576).toFixed(2) + " MB";
    } else if (bytes >= 1024) {
      bytes = (bytes / 1024).toFixed(2) + " KB";
    } else if (bytes > 1) {
      bytes = bytes + " bytes";
    } else if (bytes == 1) {
      bytes = bytes + " byte";
    } else {
      bytes = "0 bytes";
    }
    return bytes;
  }

  deleteFile() {
    this.proposalForm.patchValue({
      file: null,
    });
  }

  clearForm() {
    this.proposalForm = this.formBuilder.group({
      propertyId: [this.deal.property?.id, [Validators.required]],
      lead: [null],
      contact: [null],
      // clientName: [null, [Validators.required]],
      clientName: [this.getDefaultContactName()],
      proposalDate: [new Date()],
      value: [this.deal.property?.price, [Validators.required]],
      obs: [null],
      file: [null],
    });
  }

  private getDefaultContactName(): string | null {
    const contact: any = this.deal?.contact;
    if (!contact) {
      return null;
    }

    const fallbackName = [contact?.firstName, contact?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();

    return contact?.name ?? contact?.fullName ?? (fallbackName || null);
  }

  submitNewProposal() {
    this.submited = true;
    if (this.proposalForm.valid) {
      let formData = new FormData();
      // formData.append('data', JSON.stringify(fData));
      formData.append("clientName", this.proposalForm.get("clientName")?.value);
      // FormData converte null/undefined nas strings "null"/"undefined", que o
      // back-end tenta ler como UUID. Sem imovel nao ha proposta possivel.
      const propertyId = this.proposalForm.get("propertyId")?.value;
      if (!propertyId) {
        this.submited = false;
        this.errorMsg(this.translateService.instant("t-error-creating"));
        return;
      }
      formData.append("propertyId", propertyId);
      formData.append("value", this.proposalForm.get("value")?.value);
      formData.append(
        "proposalDate",
        this.proposalForm.get("proposalDate")?.value,
      );
      if (this.leadId) formData.append("lead", this.leadId);
      if (this.proposalForm.get("contact")?.value)
        formData.append("contact", this.proposalForm.get("contact")?.value);

      if (this.proposalForm.get("obs")?.value)
        formData.append("obs", this.proposalForm.get("obs")?.value);

      if (this.proposalForm.get("file")?.value)
        formData.append("file", this.proposalForm.get("file")?.value);

      if (this.proposalForm.get("agencyProposal")?.value) {
        formData.append(
          "agencyProposal",
          this.proposalForm.get("agencyProposal")?.value,
        );
        formData.append(
          "partnerName",
          this.proposalForm.get("partnerName")?.value,
        );
        formData.append(
          "licenseImo",
          this.proposalForm.get("licenseImo")?.value,
        );
        formData.append(
          "saleDivision",
          this.proposalForm.get("saleDivision")?.value,
        );
      } else if (this.isInternalShareProposal) {
        // Partilha interna: envia as condições de divisão da comissão para a
        // loja angariadora validar (e a loja parceira, se houver mais de uma).
        formData.append(
          "saleDivision",
          this.proposalForm.get("saleDivision")?.value ?? 50,
        );
        if (this.internalSharePartnerStoreId) {
          formData.append(
            "partnerStoreId",
            `${this.internalSharePartnerStoreId}`,
          );
        }
      }

      if (!this.isEdit) {
        this.proposalsService.createProposal(formData).subscribe({
          next: (data) => {
            this.submited = false;
            Swal.fire({
              //position: 'top-end',
              icon: "success",
              title: this.translateService.instant("t-proposal-created"),
              showConfirmButton: false,
              timer: 1500,
            });
            this.modalService.dismissAll();
            this.eventService.broadcast("new-proposal-closed");
          },
          error: (err) => {
            if (this.isPendingProposalLimitError(err)) {
              this.proposalLimitMsg(this.getLimitReachedMessage());
              return;
            }
            this.errorMsg(
              this.getTranslatedErrorMessage(err) ??
                this.translateService.instant("t-error-creating"),
            );
          },
        });
      } else {
        this.proposalsService
          .updateProposal(formData, this.proposal?.proposal?.id)
          .subscribe({
            next: (data) => {
              this.submited = false;
              Swal.fire({
                //position: 'top-end',
                icon: "success",
                title: this.translateService.instant("t-proposal-created"),
                showConfirmButton: false,
                timer: 1500,
              });
              this.modalService.dismissAll();
              this.eventService.broadcast("new-proposal-closed");
            },
            error: (err) => {
              this.errorMsg(
                this.getTranslatedErrorMessage(err) ??
                  this.translateService.instant("t-error-updating"),
              );
            },
          });
      }
    }
  }

  // O exception filter global aninha as respostas de HttpException:
  // { message: { statusCode, message: '<chave i18n>', error } }. Quando a
  // mensagem é uma chave t-... com tradução, mostra-a em vez do erro genérico.
  private getTranslatedErrorMessage(error: any): string | null {
    const messagePayload = error?.error?.message;
    const messageKey =
      typeof messagePayload === "string"
        ? messagePayload
        : messagePayload?.message;
    if (typeof messageKey !== "string" || !messageKey.startsWith("t-")) {
      return null;
    }
    const translated = this.translateService.instant(messageKey);
    return translated && translated !== messageKey ? translated : null;
  }

  /**
   * Title sweet alert
   * @param Error Msg modal content
   */
  errorMsg(errorMsg: string) {
    Swal.fire({
      title: this.translateService.instant("t-something-wrong"),
      text: errorMsg,
      icon: "warning",
      confirmButtonColor: "rgb(54, 69, 116)",
      confirmButtonText: this.translateService.instant("t-close"),
    });
  }

  /**
   * Title sweet alert
   * @param Error Msg modal content
   */
  proposalLimitMsg(errorMsg: string) {
    Swal.fire({
      title: errorMsg,
      text: "",
      icon: "info",
      confirmButtonColor: "rgb(54, 69, 116)",
      confirmButtonText: this.translateService.instant("t-close"),
    });
  }

  closeModal() {
    this.modalService.dismissAll();
    this.proposalForm.reset();
    this.eventService.broadcast("new-proposal-closed");
  }

  nextStep() {
    if (this.currentStep === 0) {
      const proposers = (this.proposers ?? []).filter(Boolean);

      if (proposers.length === 0) {
        this.showError["proposers"] = true;
        return;
      }

      const hasLegal = proposers.some((p) => p.type == "LEGAL");
      const hasBeneficiary = proposers.some(
        (p) => p.type == "EFFECTIVE_BENEFICIARY",
      );
      if (hasLegal && !hasBeneficiary) {
        this.showError["beneficial"] = true;
        return;
      }

      this.fullProposalForm.patchValue({
        proposers,
      });

      this.fullProposalForm.get("proposers")?.updateValueAndValidity();
    } else if (this.currentStep === 1) {
      this.fullProposalForm.patchValue({
        proposers: this.proposers,
      });
      this.fullProposalForm.get("proposers")?.updateValueAndValidity();
      if (!this.fullProposalForm.valid) {
        this.fullProposalForm.markAllAsTouched();
        return;
      }
    }
    if (this.currentStep < this.steps.length - 1) {
      this.currentStep++;
    }
  }

  previousStep() {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  isSingular() {
    return (
      this.proposerForm.get("type")?.value == "INDIVIDUAL" ||
      this.proposerForm.get("type")?.value == "EFFECTIVE_BENEFICIARY" ||
      this.proposerForm.get("type")?.value == "FIATHER"
    );
  }

  isColetive() {
    return this.proposerForm.get("type")?.value == "LEGAL";
  }

  onTypeChange() {
    const type = this.proposerForm.get("type")?.value;
    if (
      type == "INDIVIDUAL" ||
      type == "EFFECTIVE_BENEFICIARY" ||
      type == "FIATHER"
    ) {
      this.proposerForm.get("name")?.addValidators(Validators.required);
      this.proposerForm
        .get("fiscalIdentification")
        ?.addValidators(Validators.required);
      this.proposerForm.get("docType")?.addValidators(Validators.required);
      this.proposerForm.get("docNumber")?.addValidators(Validators.required);
      this.proposerForm
        .get("docExpiration")
        ?.addValidators(Validators.required);

      this.proposerForm.get("company")?.removeValidators(Validators.required);
      this.proposerForm
        .get("companyFiscalIdentification")
        ?.removeValidators(Validators.required);
      this.proposerForm
        .get("permanentRegistry")
        ?.removeValidators(Validators.required);
    } else if (type == "LEGAL") {
      // Pessoa coletiva
      this.proposerForm.get("name")?.removeValidators(Validators.required);
      this.proposerForm
        .get("fiscalIdentification")
        ?.removeValidators(Validators.required);
      this.proposerForm.get("docType")?.removeValidators(Validators.required);
      this.proposerForm.get("docNumber")?.removeValidators(Validators.required);
      this.proposerForm
        .get("docExpiration")
        ?.removeValidators(Validators.required);
      this.proposerForm.get("company")?.addValidators(Validators.required);
      this.proposerForm
        .get("companyFiscalIdentification")
        ?.addValidators(Validators.required);
      this.proposerForm
        .get("permanentRegistry")
        ?.addValidators(Validators.required);
    }
    [
      "name",
      "fiscalIdentification",
      "docType",
      "docNumber",
      "docExpiration",
      "company",
      "companyFiscalIdentification",
      "permanentRegistry",
    ].forEach((field) => {
      this.proposerForm.get(field)?.updateValueAndValidity();
    });
  }

  submitProposer(modal: any) {
    if (this.proposerForm.valid) {
      const data = this.proposerForm.value;
      if (
        !this.proposers.some(
          (p) => p.fiscalIdentification === data?.fiscalIdentification,
        )
      ) {
        this.proposers.push(data);
      } else {
        this.proposers = this.proposers.filter(
          (p) => p.fiscalIdentification !== data?.fiscalIdentification,
        );
        this.proposers.push(data);
      }
      if (this.showError) {
        this.showError = {};
      }
      this.fullProposalForm.patchValue({ proposers: this.proposers });
      this.fullProposalForm.get("proposers")?.updateValueAndValidity();
      this.broadcastProposalDraft();
      modal.dismiss();
    }
  }

  get proposerFormControls() {
    return this.proposerForm.controls;
  }

  get proposalFormControls() {
    return this.fullProposalForm.controls;
  }

  openProposerForm(content: any, data: any) {
    if (!data && this.proposers.length === 0) {
      data = this.deal.contact;
    }
    this.initProposerForm(data);
    this.modalService.open(content, {
      size: "xl",
      backdropClass: "modal-z",
      windowClass: "modal-z",
    });
  }

  initProposerForm(data: any) {
    const date = data?.docExpiration
      ? new Date(data?.docExpiration)
      : new Date();
    const docExpiration = date.toISOString().split("T")[0];
    this.proposerForm = this.formBuilder.group({
      type: [data?.type ?? "INDIVIDUAL"],
      name: [
        data?.name
          ? data?.name
          : data?.fullName
            ? data?.fullName
            : data?.firstName
              ? data?.lastName
                ? data?.firstName + " " + data?.lastName
                : data?.firstName
              : null,
        Validators.required,
      ],
      fiscalIdentification: [data?.fiscalIdentification, Validators.required],
      docType: [data?.docType, Validators.required],
      docNumber: [data?.docNumber, Validators.required],
      docExpiration: [docExpiration, Validators.required],
      maritalStatus: [data?.maritalStatus],
      propertyRegime: [data?.propertyRegime],
      naturalness: [data?.naturalness],
      nationality: [data?.nationality],
      email: [data?.email, Validators.email],
      phone: [data?.phone],
      address: [data?.address],
      city: [data?.city],
      postalCode: [data?.postalCode],
      country: [data?.country ? data?.country : data?.fiscalCountry],
      fiscalAddress: [data?.fiscalAddress],
      fiscalCity: [data?.fiscalCity],
      fiscalPostalCode: [data?.fiscalPostalCode],
      fiscalCountry: [data?.fiscalCountry],
      occupation: [data?.occupation],
      employer: [data?.employer],
      actionType: [data?.actionType ?? "SELF"],
      actionTypeOther: [data?.actionTypeOther],
      politicalPositions: [data?.politicalPositions === true ? true : false],
      politicalDesc: [data?.politicalDesc],
      company: [data?.company],
      companyFiscalIdentification: [data?.companyFiscalIdentification],
      permanentRegistry: [data?.permanentRegistry],
    });
  }

  removeProposer(proposer: any) {
    this.proposers = this.proposers.filter(
      (p) =>
        p.name !== proposer.name &&
        p.fiscalIdentification !== proposer.fiscalIdentification,
    );
  }

  generateProposal() {
    this.spinnerLoading = true;
    this.proposalData = {
      ...this.fullProposalForm.value,
      proposers: this.proposers,
    };
    this.showProposal = true;
  }

  private resolveProposalBusinessType(): "SALE" | "RENT" {
    const property = this.deal?.property ?? this.proposal?.property ?? {};
    const normalizedFromText = this.normalizeBusinessType(
      property?.businessType ??
        property?.propertyBusinessType?.description ??
        property?.businessTypeData?.description,
    );

    if (normalizedFromText) {
      return normalizedFromText;
    }

    const businessTypeId = Number(
      property?.businessType ??
        property?.propertyBusinessTypeId ??
        property?.propertyBusinessType?.id ??
        property?.businessTypeData?.id,
    );
    const rentTypeId = Number(property?.typeRent);
    if (
      Number.isFinite(businessTypeId) &&
      businessTypeId > 0 &&
      Number.isFinite(rentTypeId) &&
      rentTypeId > 0
    ) {
      return businessTypeId === rentTypeId ? "RENT" : "SALE";
    }

    return "SALE";
  }

  private normalizeBusinessType(value: any): "SALE" | "RENT" | null {
    if (value === null || value === undefined) {
      return null;
    }

    const normalizedValue = `${value}`.trim().toUpperCase();
    if (!normalizedValue) {
      return null;
    }

    if (
      normalizedValue.includes("SALE") ||
      normalizedValue.includes("SELL") ||
      normalizedValue.includes("VENDA")
    ) {
      return "SALE";
    }

    if (
      normalizedValue.includes("RENT") ||
      normalizedValue.includes("LEASE") ||
      normalizedValue.includes("ARREND")
    ) {
      return "RENT";
    }

    return null;
  }

  private resolveMediationStoreName(rawStoreValue: any): string | null {
    const rawValue = this.normalizeStoreValue(rawStoreValue);
    if (rawValue && !this.looksLikeStoreId(rawValue)) {
      return rawValue;
    }

    const dealStoreValue = this.normalizeStoreValue(this.deal?.property?.store);
    if (dealStoreValue && !this.looksLikeStoreId(dealStoreValue)) {
      return dealStoreValue;
    }

    const storeDataValue = this.normalizeStoreValue(
      this.deal?.property?.storeData?.name ??
      this.deal?.property?.storeData?.store?.name ??
      this.deal?.property?.storeData,
    );
    if (storeDataValue && !this.looksLikeStoreId(storeDataValue)) {
      return storeDataValue;
    }

    const agentStoreValue = this.normalizeStoreValue(this.deal?.agent?.store);
    if (agentStoreValue && !this.looksLikeStoreId(agentStoreValue)) {
      return agentStoreValue;
    }

    return rawValue ?? dealStoreValue ?? storeDataValue ?? agentStoreValue ?? null;
  }

  private normalizeStoreValue(value: any): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === "object") {
      const candidateName =
        value?.name ??
        value?.store?.name ??
        value?.title ??
        value?.description ??
        value?.label;

      if (candidateName !== null && candidateName !== undefined) {
        const normalizedName = String(candidateName).trim();
        if (normalizedName.length > 0) {
          return normalizedName;
        }
      }

      const candidateId = value?.id ?? value?.storeId;
      if (candidateId !== null && candidateId !== undefined) {
        const normalizedId = String(candidateId).trim();
        return normalizedId.length > 0 ? normalizedId : null;
      }

      return null;
    }

    const normalized = String(value).trim();
    return normalized.length > 0 ? normalized : null;
  }

  private looksLikeStoreId(value: string): boolean {
    return /^\d+$/.test(value);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}

